/** Daily-goal garland: flags on a sagging string, filled ones in the course colours. */
import { useEffect, useRef, type CSSProperties } from 'react';
import { GARLAND_FLAGS } from '../core/progress.ts';
import './garland.css';

const COLORS = ['var(--c-a1)', 'var(--c-a2)', 'var(--c-a3)'];
const VIEW_W = 170;
const VIEW_H = 24;
const SAG = 14;

function pointOnString(t: number): { x: number; y: number } {
  // Quadratic Bézier (4,3) → control (85, 3 + SAG) → (166,3); x is linear in t.
  const x = 4 + t * (VIEW_W - 8);
  const y = 3 + 2 * (1 - t) * t * SAG;
  return { x, y };
}

export function Garland({
  filled,
  width = VIEW_W,
  label,
}: {
  filled: number;
  width?: number;
  label: string;
}) {
  const shownRef = useRef(filled);
  const previous = shownRef.current;
  useEffect(() => {
    shownRef.current = filled;
  }, [filled]);

  return (
    <svg
      className="garland"
      width={width}
      height={(width / VIEW_W) * VIEW_H}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="img"
      aria-label={label}
    >
      <path d={`M4 3 Q${VIEW_W / 2} ${3 + SAG} ${VIEW_W - 4} 3`} className="garland__string" />
      {Array.from({ length: GARLAND_FLAGS }, (_, i) => {
        const t = (i + 0.5) / GARLAND_FLAGS;
        const { x, y } = pointOnString(t);
        const isFilled = i < filled;
        const isNew = isFilled && i >= previous;
        const style = {
          fill: isFilled ? COLORS[i % COLORS.length] : 'var(--sheet)',
          transformOrigin: `${x}px ${y}px`,
          animationDelay: isNew ? `${(i - previous) * 70}ms` : undefined,
        } as CSSProperties;
        return (
          <path
            key={i}
            className={`garland__flag${isFilled ? '' : ' garland__flag--empty'}${isNew ? ' garland__flag--new' : ''}`}
            d={`M${x - 5.5} ${y} L${x + 5.5} ${y} L${x} ${y + 11} Z`}
            style={style}
          />
        );
      })}
    </svg>
  );
}
